import { useRef, useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { debounce } from '@/utils';
import { DateRange } from '@/components/common/DateRangePicker';
import { useGetPendingMarketsQuery } from '@/store/api/eventApi';
import { MarketRunners, useLazyGetMarketRunnerQuery } from '@/store/api/runnersApi';
import {
  DeclareResultPayload,
  useDeclareResultMutation,
  useRollbackResultMutation,
} from '@/store/api/betResultApi';
import { PendingMarketItem } from '@/types/pendingMarket';
import { useGetRollbackMarketsQuery } from '@/store/api/eventApi';
import dayjs from 'dayjs';

export type ResultValue = {
  status: 'Winner' | 'Looser' | 'None';
  selectionId: string;
};

const sportOptions = [
  { label: 'All', value: 'all' },
  { label: 'Cricket', value: 'cricket' },
  { label: 'Football', value: 'football' },
  { label: 'Tennis', value: 'tennis' },
  { label: 'Horse Racing', value: 'horse_racing' },
    { label: 'GreyHound Racing', value: 'greyhound_racing' },
  { label: 'Basketball', value: 'basketball' },
  { label: 'Boxing', value: 'boxing' },
  { label: 'Baseball', value: 'baseball' },
  { label: 'American Football', value: 'american_football' },
  { label: 'Esports', value: 'esports' },
];
export type ResultTabType = 'pending' | 'rollback';
type ResultActionType = 'declare' | 'rollback';

export const useEventListing = () => {
  const [isPollingEnabled, setIsPollingEnabled] = useState(true);
  const [sport, setSport] = useState<string>('all');
  const [activeResultTab, setActiveResultTab] = useState<ResultTabType>('pending');

  // pagination & filters
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [dateRange, setDateRange] = useState<DateRange>({
    from: dayjs().startOf('day').toDate(),
    to: dayjs().endOf('day').toDate(),
  });

  // Track loading state specifically for user actions (pagination, filters, tab changes)
  // This prevents the table from flashing a skeleton every 5 seconds during background polls
  const [isUserActionLoading, setIsUserActionLoading] = useState(false);

  // modal state
  const [open, setOpen] = useState(false);
  const [rollbackConfirmOpen, setRollbackConfirmOpen] = useState(false);
  const [selectedEventName, setSelectedEventName] = useState('');
  const [runners, setRunners] = useState<MarketRunners>({} as MarketRunners);
  const [eventDetails, setEventDetails] = useState<PendingMarketItem>();

  // APIs
  const { data, isLoading, isFetching } = useGetPendingMarketsQuery(
    {
      page,
      limit,
      search,
      ...(dateRange?.from && { fromDate: dayjs(dateRange.from).toISOString() }),
      ...(dateRange?.to && { toDate: dayjs(dateRange.to).toISOString() }),
      sport: sport !== 'all' ? sport : undefined,
    },
    {
      skip: activeResultTab !== 'pending',
      pollingInterval: isPollingEnabled && activeResultTab === 'pending' ? 5000 : 0,
    }
  );

  const {
    data: rollbackData,
    isLoading: rollbackLoading,
    isFetching: rollbackFetching,
  } = useGetRollbackMarketsQuery(
    {
      page,
      limit,
      search,
      ...(dateRange?.from && { fromDate: dayjs(dateRange.from).toISOString() }),
      ...(dateRange?.to && { toDate: dayjs(dateRange.to).toISOString() }),
      sport: sport !== 'all' ? sport : undefined,
    },
    {
      skip: activeResultTab !== 'rollback',
      pollingInterval: isPollingEnabled && activeResultTab === 'rollback' ? 5000 : 0,
    }
  );

  const [fetchRunner, { isLoading: runnersLoading }] = useLazyGetMarketRunnerQuery();
  const [declareResult, { isLoading: betResultLoading }] = useDeclareResultMutation();
  const [rollbackResult, { isLoading: rollbackResultLoading }] = useRollbackResultMutation();
  const isActionLoading = betResultLoading || rollbackResultLoading;

  // Reset user action loading when the API request completes
  useEffect(() => {
    if (!isFetching && !rollbackFetching) {
      setIsUserActionLoading(false);
    }
  }, [isFetching, rollbackFetching]);

  // Combine initial load states with user action loading state
  const isTableLoading = isLoading || rollbackLoading || isUserActionLoading;

  const items = activeResultTab === 'pending' ? data?.markets || [] : rollbackData?.markets || [];

  const totalItems =
    activeResultTab === 'pending'
      ? data?.pagination?.totalItems || 0
      : rollbackData?.pagination?.totalItems || 0;

  // Debounced search
  const debouncedSearch = useRef(
    debounce((value: string) => {
      setSearch(value);
      setPage(1);
      setIsUserActionLoading(true); // Trigger loading
    }, 500)
  ).current;

  // Open Result Modal
  const handleOpenResult = async (row: PendingMarketItem) => {
    try {
      setSelectedEventName(row.eventName);
      setEventDetails(row);

      if (activeResultTab === 'rollback') {
        setRollbackConfirmOpen(true);
        return;
      }

      let runnersData = {} as MarketRunners;

      if (row.marketType?.toLowerCase() === 'fancy') {
        runnersData = {
          id: '',
          externalId: '',
          eventId: '',
          status: '',
          runner: [
            {
              runnerName: row.selection || '',
              handicap: 0,
              selectionId: '',
              sortPriority: '',
              runnerId: '',
            },
          ],
        };
      } else {
        const response = await fetchRunner({
          eventExternalId: row.eventExternalId,
          marketId: row.marketId,
        }).unwrap();

        runnersData = response.marketRunners;
      }

      setRunners(runnersData);
      setOpen(true);
    } catch (error) {
      console.error(error);
    }
  };

  // Declare Result
  const handleResultAction = async (
    action: ResultActionType,
    data: Record<string, ResultValue>,
    runs: string,
    options?: { eventDetails?: PendingMarketItem; isCancel?: boolean }
  ): Promise<boolean> => {
    const details = options?.eventDetails ?? eventDetails;
    if (!details) return false;

    const { marketName, marketType, eventExternalId, marketId } = details;

    const isCancel = options?.isCancel;
    const basePayload: DeclareResultPayload = {
      market: marketName || '',
      marketType: marketType || '',
      eventId: eventExternalId || '',
      marketId: marketId || '',
    };

    const payload: DeclareResultPayload =
      action === 'rollback'
        ? basePayload
        : (() => {
            const winner = Object.values(data).find((r) => r.status === 'Winner');
            const isFancy = marketType?.toLowerCase() === 'fancy';
            const isSession = marketName?.toLowerCase() === 'session';

            let selectionId = '';

            if (isCancel) {
              selectionId = '-999';
            } else if (winner?.selectionId) {
              selectionId = winner.selectionId;
            } else if (isFancy) {
              selectionId = marketId || '';
            }

            if (isFancy && !isSession && !isCancel && !winner && selectionId) {
              selectionId = `${selectionId}_loss`;
            }

            const result = isCancel ? '-999' : runs ? runs : selectionId;

            return {
              ...basePayload,
              selectionId,
              result,
            };
          })();

    try {
      const res =
        action === 'declare'
          ? await declareResult(payload).unwrap()
          : await rollbackResult(payload).unwrap();

      toast.success(
        res?.message ||
          (action === 'declare'
            ? 'Result declared successfully!'
            : 'Result rolled back successfully!')
      );

      setOpen(false);
      setRollbackConfirmOpen(false);
      return true;
    } catch (err: any) {
      toast.error(err?.data?.message || err?.message || 'Action failed');
      return false;
    }
  };

  // Wrapper functions to trigger loading state on user interactions
  const updatePage = (p: number) => {
    setPage(p);
    setIsUserActionLoading(true);
  };

  const updateLimit = (l: number) => {
    setLimit(l);
    setIsUserActionLoading(true);
  };

  const updateDateRange = (range: DateRange) => {
    setDateRange(range);
    setIsUserActionLoading(true);
  };

  const handleSportChange = (value: string) => {
    setSport(value);
    setIsUserActionLoading(true);
  };

  const updateActiveResultTab = (tab: ResultTabType) => {
    setActiveResultTab(tab);
    setIsUserActionLoading(true);
  };

  return {
    // table data
    items,
    totalItems,
    isTableLoading,
    betResultLoading: isActionLoading,

    // pagination & filters
    page,
    limit,
    setPage: updatePage, // Return wrapped function
    setLimit: updateLimit, // Return wrapped function
    dateRange,
    setDateRange: updateDateRange, // Return wrapped function
    debouncedSearch,
    isPollingEnabled,
    setIsPollingEnabled,

    // modal
    open,
    setOpen,
    rollbackConfirmOpen,
    setRollbackConfirmOpen,
    selectedEventName,
    lastResult: eventDetails?.result || '-',
    marketNameLabel: eventDetails?.marketName || '',
    runners,
    marketType: (eventDetails?.marketType || '').toLowerCase(),
    marketName: (eventDetails?.marketName || '').toLowerCase(),

    // actions
    handleOpenResult,
    handleResultAction,

    // FILTERS
    sport,
    sportOptions,
    handleSportChange,
    activeResultTab,
    setActiveResultTab: updateActiveResultTab, // Return wrapped function
  };
};
